import { AuthenticatorContextProvider } from '@ms/yammer-authenticator-context';
import { getAuthenticator } from '@ms/yammer-data/dist/authentication';
import { Store } from '@ms/yammer-data/dist/state/types';
import { ExperimentContextProvider } from '@ms/yammer-experiments-context';
import {
  useAndReportExperimentTreatment,
  useExperimentTreatmentWithoutReporting as useExperimentTreatment,
  useReportExperimentTreatmentCallback,
} from '@ms/yammer-hooks/dist/experiments';
import { useIsFeatureRolloutEnabled } from '@ms/yammer-hooks/dist/featureRollouts';
import { LazyRelayAppWrapper } from '@ms/yammer-relay/dist/environment';
import { PageSectionRefsProvider } from '@ms/yammer-web-components/dist/app/PageSectionRefs';
import { ReduxTelemetryContextProvider } from '@ms/yammer-web-components/dist/telemetry';
import { ReduxTranslationsContextProvider } from '@ms/yammer-web-components/dist/translations';
import { AnnouncerProvider } from '@ms/yammer-web-core-ui/dist/components/Announcer';
import GriffelNoopRendererProvider from '@ms/yammer-web-core-ui/dist/components/GriffelNoopRendererProvider';
import { basenameForTeamsApp, isLoadedWithGlammerOrEngageDomain } from '@ms/yammer-web-support/dist/location';
import AppError, { AppErrorBoundary } from 'components/AppError';
import React, { FC } from 'react';
import { HelmetProvider } from 'react-helmet-async';
import { Provider } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { CompatRouter } from 'react-router-dom-v5-compat';
import { YammerTeamsState } from 'state/types';

import App from '../App';
import EscapeKeyNavigationHandler from '../EscapeKeyNavigationHandler';

import { applyMainStyles } from './Main.styles.legacy';
import { getLazyRelayAppWrapperProps } from './getLazyRelayAppWrapperProps';

const experimentContext = {
  useReportExperimentTreatmentCallback,
  useIsFeatureRolloutEnabled,
  useExperimentTreatment,
  useAndReportExperimentTreatment,
};

interface MainProps {
  readonly store: Store<YammerTeamsState>;
  readonly error: unknown;
  readonly etag?: string;
}

const Main: FC<MainProps> = ({ store, error, etag }) => {
  const routeBasename = isLoadedWithGlammerOrEngageDomain() ? basenameForTeamsApp : '';
  const children =
    error != null ? (
      <AppErrorBoundary>
        <AppError error={error} />
      </AppErrorBoundary>
    ) : (
      <App />
    );

  applyMainStyles();

  return (
    <GriffelNoopRendererProvider>
      <Provider store={store}>
        <ReduxTranslationsContextProvider>
          <ReduxTelemetryContextProvider>
            <AuthenticatorContextProvider authenticator={getAuthenticator()}>
              <ExperimentContextProvider {...experimentContext}>
                <LazyRelayAppWrapper {...getLazyRelayAppWrapperProps(store, etag)}>
                  <HelmetProvider>
                    <BrowserRouter basename={routeBasename}>
                      <CompatRouter>
                        <PageSectionRefsProvider>
                          <EscapeKeyNavigationHandler>
                            <AnnouncerProvider>{children}</AnnouncerProvider>
                          </EscapeKeyNavigationHandler>
                        </PageSectionRefsProvider>
                      </CompatRouter>
                    </BrowserRouter>
                  </HelmetProvider>
                </LazyRelayAppWrapper>
              </ExperimentContextProvider>
            </AuthenticatorContextProvider>
          </ReduxTelemetryContextProvider>
        </ReduxTranslationsContextProvider>
      </Provider>
    </GriffelNoopRendererProvider>
  );
};

export default Main;
